import { makeAutoObservable } from 'mobx';
import { DATA } from './data';
import { IMAGES } from './data/images';
import { Airport } from './airport';
import { AirplaneUpgradeType, AIRPLANE_UPGRADES, AirplaneColor, AIRPLANE_DATA } from './data/airplaneData';
import { AirportSlug } from './data/airportData';
import { TripData } from './data.type';
import { uuid } from './helpers';

export class Airplane {
  public status: 'idle' | 'flying' = 'idle';
  public currentTrip: TripData | null = null;
  public upgrades: { [k in AirplaneUpgradeType]: number } = { capacity: 0, speed: 0, ticket_price: 0 };
  public color: AirplaneColor = 'blue';

  private tripTimeout: number | null = null;

  constructor(
    public id: number,
    public name: string,
    public airportSlug: AirportSlug,
    public priceStep: number,
  ) {
    makeAutoObservable(this);
  }

  quickTrip() {
    const trips: TripData[] = [];
    const airport = DATA.airportBySlug(this.airportSlug);
    const otherAirports = DATA.airports.filter((a) => a.slug !== airport.slug && a.isAvailable);
    otherAirports.forEach((otherAirport) => {
      trips.push(this.computeTripData(airport, otherAirport));
    });
    const tripsDescendingProfit = trips.sort((a, b) => b.profit - a.profit);
    const trip = tripsDescendingProfit[0];
    if (trip) {
      this.beginTrip(trip);
    }
  }

  beginTrip(trip: TripData) {
    this.airportSlug = trip.toAirport.slug;
    this.status = 'flying';
    this.currentTrip = trip;
    DATA.addMoney(trip.profit);
    trip.fromAirport.substractPassengers(trip.toAirport.slug, trip.passengers);
    this.tripTimeout = window.setTimeout(() => {
      this.endTrip(trip);
    }, trip.lengthMs);
  }

  endTrip(trip: TripData) {
    this.status = 'idle';
    this.currentTrip = null;
    DATA.addTransportedPassangers(trip.passengers);
  }

  getUpgradeValue(type: AirplaneUpgradeType) {
    return AIRPLANE_UPGRADES[type].valueFn(this.upgrades[type]);
  }

  getUpgradePrice(type: AirplaneUpgradeType) {
    return Math.round(AIRPLANE_UPGRADES[type].priceFn(this.upgrades[type], this.priceStep));
  }

  increaseUpgradeLevel(type: AirplaneUpgradeType) {
    this.upgrades[type]++;
    this.upgrades[type] = Math.min(this.upgrades[type], AIRPLANE_UPGRADES[type].maxValue);
  }

  computeTripData(fromAirport: Airport, toAirport: Airport) {
    const passengersToDestination = fromAirport.passengers[toAirport.slug] || 0;
    const distance = fromAirport.distanceTo(toAirport);
    const distanceMultiplier = distance / 200 + 1; // from 1..1.5
    const ticketPrice = Math.round(
      this.getUpgradeValue('ticket_price') * distanceMultiplier * fromAirport.getUpgradeValue('ticket_price_multiplier'),
    );
    const passengers = Math.min(this.getUpgradeValue('capacity'), passengersToDestination);

    const tripData: TripData = {
      id: uuid(),
      fromAirport,
      toAirport,
      airplane: this,
      profit: ticketPrice * passengers,
      lengthMs: AIRPLANE_DATA.tripLengthMs(fromAirport, toAirport, this.getUpgradeValue('speed')),
      passengers: passengers,
    };

    return tripData;
  }

  dispose() {
    if (this.tripTimeout) {
      window.clearTimeout(this.tripTimeout);
    }
  }
}
